export interface NavItem {
  id: string;
  label: string;
}

export const navItems: NavItem[] = [
  { id: "directions", label: "核心方向" },
  { id: "modes", label: "播放方式" },
  { id: "library", label: "影片片单" },
  { id: "genres", label: "题材分类" },
  { id: "preview", label: "抢先看" },
  { id: "series", label: "全集在线" },
  { id: "guide", label: "观看指南" },
  { id: "faq", label: "常见问题" },
  { id: "updates", label: "更新动态" },
];

export interface Direction {
  icon: string;
  title: string;
  keyword: string;
  summary: string;
  points: string[];
}

export const directions: Direction[] = [
  {
    icon: "ri-play-circle-line",
    title: "在线观看",
    keyword: "星辰影院在线观看",
    summary: "打开页面即可进入播放场景，无需额外安装客户端，选集与播放进度在同一处完成。",
    points: ["直接进入播放场景", "支持选集与进度记忆", "页面内完成主要操作"],
  },
  {
    icon: "ri-vip-diamond-line",
    title: "免费观看",
    keyword: "星辰影院免费观看",
    summary: "片单中标注“免费观看”的内容可直接浏览，无需额外步骤即可进入正片。",
    points: ["标注即可看", "片单按免费维度筛选", "标注与内容一一对应"],
  },
  {
    icon: "ri-hd-line",
    title: "高清播放",
    keyword: "星辰影院高清播放",
    summary: "提供清晰度档位说明，画面与音量可在播放页内按需调整，兼顾清晰与流畅。",
    points: ["清晰度档位切换", "音量与画面独立调节", "弱网环境自动降档"],
  },
  {
    icon: "ri-smartphone-line",
    title: "手机在线",
    keyword: "星辰影院手机在线",
    summary: "为小屏浏览特别整理版式，竖屏单手可完成选集与播放，横屏自动铺满画面。",
    points: ["竖屏单手操作", "横屏自动全屏", "触控手势说明"],
  },
];

export interface PlayMode {
  id: string;
  label: string;
  icon: string;
  headline: string;
  desc: string;
  features: { title: string; text: string }[];
  fit: string;
}

export const playModes: PlayMode[] = [
  {
    id: "online",
    label: "在线观看",
    icon: "ri-play-circle-line",
    headline: "打开即播，不装客户端",
    desc: "在线观看强调“少一步到达”。进入片单选中内容后直接进入播放场景，选集、切换清晰度、续播都在同一页面完成，浏览器打开即可用。",
    features: [
      { title: "直达播放场景", text: "从片单点击进入，不经过多余的中转页面，减少等待与跳转。" },
      { title: "进度自动记忆", text: "同一浏览器再次进入时，可从上次停下的位置继续，无需手动标记。" },
      { title: "选集就近切换", text: "剧集类内容在播放页内即可切换集数，保持当前清晰度与音量设置。" },
    ],
    fit: "适合习惯在电脑浏览器上连续追更、希望快速切换内容方向的用户。",
  },
  {
    id: "free",
    label: "免费观看",
    icon: "ri-vip-diamond-line",
    headline: "标注清晰，可直接浏览",
    desc: "片单为每部内容标注了是否支持免费观看。标注为免费的内容可直接进入正片，筛选面板也能一键只看免费方向，方便快速定位。",
    features: [
      { title: "标注即所得", text: "卡片上的标识与内容属性一一对应，看到标识就能判断能否直接观看。" },
      { title: "一键只看免费", text: "在片单筛选面板切换“免费观看”即可过滤，无需逐条确认。" },
      { title: "与内容对应", text: "免费标识只随内容本身变化，不因浏览设备或时间而临时改变。" },
    ],
    fit: "适合想先低成本试看几部内容、再决定是否长期浏览的用户。",
  },
  {
    id: "hd",
    label: "高清播放",
    icon: "ri-hd-line",
    headline: "清晰度可选，画面更稳",
    desc: "高清播放围绕画面质量整理。播放页提供清晰度档位，可按网络状况手动切换；弱网时会先保流畅，再逐步恢复清晰度，避免长时间卡顿。",
    features: [
      { title: "档位手动切换", text: "在播放页选择清晰度，切换过程不影响当前进度与选集状态。" },
      { title: "画面音量独立", text: "清晰度与音量分别调节，互不干扰，适配外放与耳机两种场景。" },
      { title: "弱网自动降档", text: "网络波动时优先保证播放连续，稳定后自动回到更高清晰度。" },
    ],
    fit: "适合对画面有要求、网络环境时好时坏，希望观看过程更稳定的用户。",
  },
  {
    id: "mobile",
    label: "手机在线",
    icon: "ri-smartphone-line",
    headline: "小屏友好，单手也能操作",
    desc: "手机在线针对小屏浏览做了版式整理。竖屏时选集、进度条与清晰度按钮集中在拇指可及的区域；横屏会自动铺满画面，接近全屏观感。",
    features: [
      { title: "竖屏单手可及", text: "关键控件集中在屏幕下部，单手即可完成选集与播放控制。" },
      { title: "横屏自动铺满", text: "旋转手机后画面自动适配，隐藏多余元素，专注观看本身。" },
      { title: "触控手势说明", text: "提供手势对照说明，左右滑动调进度、上下滑动调音量的用法一目了然。" },
    ],
    fit: "适合通勤、睡前等碎片时间，用手机随时接续观看的用户。",
  },
];

export interface Genre {
  id: string;
  name: string;
  icon: string;
  count: number;
  desc: string;
}

export const genres: Genre[] = [
  { id: "科幻", name: "科幻", icon: "ri-rocket-2-line", count: 2, desc: "太空、深海与未来设定，侧重想象与悬念。" },
  { id: "悬疑", name: "悬疑", icon: "ri-search-eye-line", count: 3, desc: "线索层层推进，适合喜欢边看边推的用户。" },
  { id: "剧情", name: "剧情", icon: "ri-quill-pen-line", count: 4, desc: "以人物与生活为核心，节奏平稳耐看。" },
  { id: "治愈", name: "治愈", icon: "ri-cup-line", count: 2, desc: "烟火气与温情日常，下饭与睡前友好。" },
  { id: "动画", name: "动画", icon: "ri-brush-line", count: 1, desc: "手绘与想象题材，合家观看无障碍。" },
  { id: "纪实", name: "纪实", icon: "ri-camera-lens-line", count: 2, desc: "真实记录与人文观察，慢节奏有质感。" },
  { id: "青春", name: "青春", icon: "ri-riding-line", count: 1, desc: "旅行、友情与成长，轻松明快。" },
  { id: "海外", name: "海外", icon: "ri-global-line", count: 3, desc: "以地区维度归类的海外片单。" },
];

export interface ShortClip {
  id: string;
  title: string;
  kind: string;
  duration: string;
  desc: string;
  image: string;
}

export const previews: ShortClip[] = [
  {
    id: "p1",
    title: "星河迷航 · 先行片段",
    kind: "剧情片段",
    duration: "2 分 10 秒",
    desc: "主角在舷窗前第一次捕捉到那段异常信号的瞬间，也第一次意识到航线已经偏离。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20still%20of%20an%20astronaut%20silhouette%20gazing%20through%20a%20spacecraft%20window%20at%20a%20glowing%20amber%20nebula%2C%20warm%20gold%20and%20crimson%20tones%2C%20dramatic%20soft%20lighting%2C%20subtle%20film%20grain%2C%20clean%20composition&width=800&height=450&seq=sc-preview-01&orientation=landscape",
  },
  {
    id: "p2",
    title: "雾都疑影 · 开场",
    kind: "剧集开场",
    duration: "1 分 45 秒",
    desc: "档案室的灯光亮起，一封被压在底层的旧卷宗被翻开，故事从一行模糊的日期开始。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20still%20of%20a%20dim%20archive%20room%20with%20a%20single%20lamp%20over%20an%20open%20old%20case%20file%2C%20warm%20gold%20and%20crimson%20tones%2C%20dramatic%20soft%20lighting%2C%20subtle%20film%20grain%2C%20clean%20composition&width=800&height=450&seq=sc-preview-02&orientation=landscape",
  },
  {
    id: "p3",
    title: "山海拾光 · 预告",
    kind: "正式预告",
    duration: "1 分 30 秒",
    desc: "拾光人走过三处山川，带回一段关于告别的记忆，预告串起了全剧的主要单元。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20still%20of%20misty%20oriental%20mountains%20with%20glowing%20floating%20lanterns%20at%20dusk%2C%20warm%20gold%20and%20crimson%20tones%2C%20dramatic%20soft%20lighting%2C%20subtle%20film%20grain%2C%20clean%20composition&width=800&height=450&seq=sc-preview-03&orientation=landscape",
  },
  {
    id: "p4",
    title: "长夜灯塔 · 氛围先导",
    kind: "先导片段",
    duration: "55 秒",
    desc: "浓雾漫过海岸，灯塔的光束在夜里一次次扫过礁石，没有人出现在画面里。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20still%20of%20a%20lighthouse%20beam%20sweeping%20over%20foggy%20coastal%20rocks%20at%20night%2C%20warm%20gold%20and%20crimson%20tones%2C%20dramatic%20soft%20lighting%2C%20subtle%20film%20grain%2C%20clean%20composition&width=800&height=450&seq=sc-preview-04&orientation=landscape",
  },
  {
    id: "p5",
    title: "春风食肆 · 片段",
    kind: "剧情片段",
    duration: "1 分 20 秒",
    desc: "热气腾腾的一锅汤面端上桌，熟客推门进来，老板头也不抬就知道他要什么。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20still%20of%20a%20steaming%20bowl%20of%20noodles%20on%20a%20wooden%20table%20in%20a%20warm%20shop%2C%20warm%20gold%20and%20crimson%20tones%2C%20dramatic%20soft%20lighting%2C%20subtle%20film%20grain%2C%20clean%20composition&width=800&height=450&seq=sc-preview-05&orientation=landscape",
  },
  {
    id: "p6",
    title: "深海回声 · 先导",
    kind: "先导片段",
    duration: "1 分 05 秒",
    desc: "潜航器下潜，声呐屏幕上出现规律跳动的回声，操作员互相对视了一眼。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20still%20of%20a%20deep%20sea%20submersible%20spotlight%20in%20dark%20water%20with%20amber%20particles%2C%20warm%20gold%20and%20crimson%20tones%2C%20dramatic%20soft%20lighting%2C%20subtle%20film%20grain%2C%20clean%20composition&width=800&height=450&seq=sc-preview-06&orientation=landscape",
  },
];

export interface SeriesInfo {
  id: string;
  title: string;
  total: number;
  updated: number;
  progress: string;
  status: "已完结" | "连载中";
  desc: string;
}

export const seriesList: SeriesInfo[] = [
  { id: "s1", title: "山海拾光", total: 30, updated: 30, progress: "已更新 30 集", status: "已完结", desc: "全篇完整，可按单元顺序观看，也支持跳看单个单元。" },
  { id: "s2", title: "雾都疑影", total: 24, updated: 24, progress: "已更新 24 集", status: "已完结", desc: "线索前后呼应较多，按顺序观看体验更完整。" },
  { id: "s3", title: "长夜灯塔", total: 16, updated: 14, progress: "已更新 14 集", status: "连载中", desc: "临近尾声，新集上线后可在播放页直接选集。" },
  { id: "s4", title: "星野南行", total: 18, updated: 18, progress: "已更新 18 集", status: "已完结", desc: "单集独立性强，日常与旅途节奏交替，随时可看。" },
  { id: "s5", title: "霓虹旧梦", total: 20, updated: 12, progress: "已更新 12 集", status: "连载中", desc: "按主题分章节展开，适合分几次观看。" },
  { id: "s6", title: "古城匠人", total: 8, updated: 8, progress: "已更新 8 集", status: "已完结", desc: "每集聚焦一门手艺，顺序观看或按兴趣跳看均可。" },
  { id: "s7", title: "春风食肆", total: 12, updated: 12, progress: "已更新 12 集", status: "已完结", desc: "单集一道家常菜，独立成篇，下饭时随手打开即可。" },
];

export interface GuideStep {
  step: string;
  title: string;
  text: string;
  icon: string;
}

export const guideSteps: GuideStep[] = [
  { step: "01", title: "确定方向", text: "先想清楚这次更看重哪个方向：是快速进入在线观看，还是先看免费、高清或手机端体验。", icon: "ri-compass-3-line" },
  { step: "02", title: "筛选片单", text: "在影片片单里按题材、类型或播放方式筛选，也可以直接搜索标题关键词，缩小到想看的内容。", icon: "ri-filter-3-line" },
  { step: "03", title: "查看详情", text: "卡片上标注了题材、地区、年份与时长，点开前先核对信息，避免中途才发现不是想看的类型。", icon: "ri-file-list-3-line" },
  { step: "04", title: "进入播放", text: "进入播放场景后按需切换清晰度与选集，进度会自动记忆，下次可在同一位置继续。", icon: "ri-play-circle-line" },
];

export interface Faq {
  q: string;
  a: string;
}

export const faqs: Faq[] = [
  {
    q: "星辰影院在线观看需要安装客户端吗？",
    a: "不需要。在线观看方向以浏览器为主，进入页面选中内容后即可进入播放场景，选集、清晰度与音量都在同一页面内完成，无需额外安装。",
  },
  {
    q: "如何判断某部内容能否免费观看？",
    a: "片单中的每张卡片都会标注播放方式。带有“免费观看”标识的内容可直接进入正片，也可以在筛选面板切换到免费维度，只显示对应内容。",
  },
  {
    q: "高清播放对网速有要求吗？",
    a: "播放页提供多个清晰度档位，可按实际网络手动切换。网络波动时会优先保证播放连续，稳定后再回到更高清晰度，不会因为一次卡顿就一直停在低画质。",
  },
  {
    q: "手机上观看会自动适配吗？",
    a: "会。竖屏时控件集中在拇指可及区域，横屏时画面自动铺满并隐藏多余元素。左右滑动可调进度、上下滑动可调音量，具体可参考手机在线说明。",
  },
  {
    q: "剧集支持从任意一集开始看吗？",
    a: "大多数剧集在播放页内可直接选集。像《古城匠人》这类单元结构的内容，顺序观看或按兴趣跳看都可以；前后呼应的剧集则建议按顺序观看。",
  },
  {
    q: "换了设备后进度还在吗？",
    a: "本页的进度记忆依附于当前浏览器，同一浏览器再次进入可继续上次位置。换设备后需要重新选择集数与位置，这是浏览现状，不代表内容本身的状态。",
  },
];

export interface UpdateItem {
  date: string;
  title: string;
  text: string;
  tag: string;
}

export const updates: UpdateItem[] = [
  { date: "10 月 06 日", title: "片单筛选面板升级", text: "新增“题材 + 播放方式”组合筛选，可同时按两个维度缩小范围，并支持标题关键词搜索。", tag: "功能" },
  { date: "10 月 02 日", title: "手机在线版式调整", text: "竖屏控件整体下移，拇指更容易触达；横屏进入后自动隐藏非必要按钮。", tag: "体验" },
  { date: "09 月 27 日", title: "高清档位说明补全", text: "补充了清晰度档位与网络状况的对应说明，方便按需手动选择。", tag: "说明" },
  { date: "09 月 21 日", title: "连载剧集进度同步", text: "《长夜灯塔》《霓虹旧梦》更新状态同步到全集在线分区，可查看当前集数。", tag: "内容" },
  { date: "09 月 15 日", title: "抢先看分区上线", text: "整理正式预告、先导片段与剧情片段，进入正片前可先了解基调。", tag: "内容" },
  { date: "09 月 08 日", title: "观看指南梳理", text: "把从选中内容到进入播放的流程拆成四步，便于新用户快速上手。", tag: "说明" },
];

export interface StatItem {
  value: string;
  label: string;
  icon: string;
}

export const stats: StatItem[] = [
  { value: "14+", label: "在架片单条目", icon: "ri-film-line" },
  { value: "8", label: "题材分类维度", icon: "ri-price-tag-3-line" },
  { value: "4", label: "播放方式方向", icon: "ri-equalizer-line" },
  { value: "7", label: "剧集连载追踪", icon: "ri-play-list-line" },
];